import { type EliteRank, type MonsterArchetype } from '@d2/core';

import { MONSTER_BAR_FILL, MONSTER_TEXTURE_PREFIX, PALETTE } from './art-spec';

/**
 * What a monster looks like — decided here, drawn elsewhere, and Phaser-free so
 * both halves can be tested without a browser.
 *
 * `art-spec.ts` holds the numbers and the colours; `placeholder-art.ts` turns
 * them into textures; this is the bit in between: the mapping from *what the
 * wire says a monster is* to *which of the twelve placeholder bodies is drawn
 * for it*, plus the one piece of arithmetic a health bar needs.
 *
 * ## No rule crosses this file
 *
 * `MonsterState` sends a champion tier as a code and a set of affixes as a
 * bitmask, and `championTierIdOf` / `eliteAffixIdsOf` turn those back into ids —
 * *"a reference, not a table"* (`packages/schema/src/monsters.ts`). What a tier
 * multiplies and what an affix does stay in `@d2/core`'s `elites.ts` and are
 * never asked for here, because nothing on screen depends on them: a ring under
 * the feet says *this one is dangerous and drops better*, and the numbers behind
 * that are the server's to apply and core's to define.
 *
 * The one judgement this module makes is which of four standings a body reads
 * as, and it is a *drawing* decision rather than a rule: it decides which
 * texture to ask for, nothing else in the client branches on it, and no server
 * needs it. Two of the four names are core's own (`EliteRank`) rather than
 * invented here, and the exclusivity core rolls — a champion carries no affixes,
 * a unique carries no tier — is deliberately not relied upon (see `badgeOf`).
 */

/**
 * What a body reads as at a glance.
 *
 * Core's two elite ranks, plus the two cases they do not name: an ordinary
 * monster, and a minion that carries its boss's affixes without being the boss.
 * That last one is the whole reason `MonsterState.leader` is on the wire — a
 * pack whose five bodies look identical is a pack whose named boss is never
 * found.
 */
export type MonsterBadge = EliteRank | 'minion' | 'ordinary';

/** Every standing, for baking every texture and for asserting there are no others. */
export const MONSTER_BADGES: readonly MonsterBadge[] = Object.freeze([
  'ordinary',
  'champion',
  'minion',
  'unique',
]);

/** The elite half of what the wire says about a monster — ids and a flag, never a table. */
export interface EliteStanding {
  /** The champion tier id, or `null` for a body that is not a champion. */
  readonly champion: string | null;
  /** The elite affix ids this body carries. A boss and its minions carry the same ones. */
  readonly affixes: readonly string[];
  /** True for the boss of a unique pack. */
  readonly leader: boolean;
}

/**
 * How a body reads, from what the wire said about it.
 *
 * **`leader` is read first**, and that is deliberate rather than incidental.
 * Core rolls a champion with no affixes and a unique boss with no tier
 * (`elites.ts`), so today the order cannot matter — but a renderer that depended
 * on that would quietly lose a boss's crown the day the roll changes, and
 * nothing on screen would say why. Leadership is the strongest claim the wire
 * makes about a body (*this is the one the pack is named after*), so it wins;
 * the affix mask alone means a pack member; a tier means a champion; and
 * everything else is an ordinary monster, which is almost every body on the
 * floor and costs nothing to say.
 */
export const badgeOf = ({ champion, affixes, leader }: EliteStanding): MonsterBadge => {
  if (leader) return 'unique';
  if (champion !== null) return 'champion';

  return affixes.length > 0 ? 'minion' : 'ordinary';
};

/** One placeholder body: which texture it is, and the three colours it is drawn from. */
export interface MonsterLook {
  readonly archetype: MonsterArchetype;
  readonly badge: MonsterBadge;
  /** The texture key. Derived from the two above, so nothing has to keep a table of names. */
  readonly key: string;
  /** The body's fill — the archetype, at a glance, and the same whatever the standing. */
  readonly fill: number;
  /** The disc on the ground, or `null` for a body that is not elite. */
  readonly ring: number | null;
  /** The mark over the head, or `null`. Only the boss of a unique pack has one. */
  readonly crown: number | null;
}

/** The archetype's own colour. The one thing a body's fill ever says. */
const FILL: Readonly<Record<MonsterArchetype, number>> = {
  melee: PALETTE.monsterMelee,
  ranged: PALETTE.monsterRanged,
  caster: PALETTE.monsterCaster,
};

/** The ring a standing is drawn with. A minion wears its own pack's colour, because it is one of them. */
const RING: Readonly<Record<MonsterBadge, number | null>> = {
  ordinary: null,
  champion: PALETTE.eliteChampion,
  minion: PALETTE.eliteUnique,
  unique: PALETTE.eliteUnique,
};

export const monsterTexture = (archetype: MonsterArchetype, badge: MonsterBadge): string =>
  `${MONSTER_TEXTURE_PREFIX}${archetype}-${badge}`;

/** Everything needed to draw one body, and everything a test needs to check it was drawn. */
export const monsterLook = (archetype: MonsterArchetype, badge: MonsterBadge): MonsterLook => ({
  archetype,
  badge,
  key: monsterTexture(archetype, badge),
  fill: FILL[archetype],
  ring: RING[badge],
  crown: badge === 'unique' ? PALETTE.eliteUnique : null,
});

/**
 * How many pixels of bar a life fraction fills.
 *
 * Two roundings, and each one answers a question the wire already answered the
 * same way:
 *
 *   - **Any life at all fills at least one pixel.** `setMonsterLife` rounds a
 *     survivor *up* precisely so an empty band means dead and nothing else
 *     (`packages/schema/src/monsters.ts`); a bar that rounded the smallest band
 *     back down to nothing would draw a corpse over a monster still worth
 *     running from, at the exact moment that matters.
 *   - **A life this wire should never describe is clamped.** `lifeBand` is a
 *     `uint8`, so a server that wrote it without `setMonsterLife` could claim
 *     more than full — and a renderer that answered by drawing past the end of
 *     its own frame would turn one bad byte into a bar across the floor.
 *     Refusing to draw is worse than drawing a full bar, so it clamps rather
 *     than throwing: this is a picture, not a rule.
 */
export const lifeBarPixels = (life: number): number => {
  if (!(life > 0)) return 0;

  return Math.max(1, Math.min(MONSTER_BAR_FILL.width, Math.round(life * MONSTER_BAR_FILL.width)));
};
